import React, { useState } from 'react';
import { ColorPicker, Image, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { Customization } from '../../../types/Cart';
import InfoText from '../../atoms/InfoText';
import MainCardItem from '../../atoms/MainCardItem';

export default function CustomizationPreview({
  customs,
}: {
  customs: Customization;
}) {
  const [isDetailsOpen, setDetailsOpened] = useState(false);
  const [imageSrc, setImageSrc] = useState<undefined | string>(undefined);
  const { t } = useTranslation();

  const columns = [
    { title: '', dataIndex: 'name', key: 'name' },
    {
      title: '',
      dataIndex: 'value',
      key: 'value',
      className: 'text-end font-semibold',
    },
  ];

  const dataSource = [
    {
      name: t('components.store.customLogo'),
      value: customs?.CUSTOM_LOGO && (
        <ImageButton
          name={customs?.CUSTOM_LOGO?.name}
          onClick={() =>
            setImageSrc(
              (customs?.CUSTOM_LOGO?.blobFileUrl ||
                customs?.CUSTOM_LOGO?.url) as string
            )
          }
        />
      ),
    },
    {
      name: t('components.store.customColor'),
      value: customs?.CUSTOM_COLOR && (
        <div className="flex items-center justify-end gap-1">
          <span>{customs?.CUSTOM_COLOR}</span>
          <ColorPicker disabled value={customs?.CUSTOM_COLOR} />
        </div>
      ),
    },
    { name: t('components.store.privateLabel'), value: customs?.PRIVATE_LABEL },
    {
      name: t('components.store.customPackaging'),
      value: customs?.CUSTOM_PACKAGING && (
        <ImageButton
          name={customs?.CUSTOM_PACKAGING?.name}
          onClick={() =>
            setImageSrc(
              (customs?.CUSTOM_PACKAGING?.blobFileUrl ||
                customs?.CUSTOM_PACKAGING?.url) as string
            )
          }
        />
      ),
    },
    {
      name: t('components.store.customDesign'),
      value: customs?.OEM_YOUR_DESIGN && (
        <ImageButton
          name={customs?.OEM_YOUR_DESIGN?.name}
          onClick={() =>
            setImageSrc(
              (customs?.OEM_YOUR_DESIGN?.blobFileUrl ||
                customs?.OEM_YOUR_DESIGN?.url) as string
            )
          }
        />
      ),
    },
  ];

  return (
    <div
      className="!mt-4 space-y-2"
      data-testid="store-order-details-customization-preview"
    >
      <div className="flex items-center gap-1">
        <InfoText
          text={t('components.store.productHasCustomization')}
          className="!text-light-alert"
        />
        <button
          className="!text-light-alert !font-semibold !text-sm underline"
          onClick={() => setDetailsOpened(!isDetailsOpen)}
          data-testid="store-order-details-custom-preview-btn"
        >
          {isDetailsOpen ? t('components.store.hideDetails') : t('components.store.seeDetails')}
        </button>
      </div>

      {isDetailsOpen && (
        <>
          <div className="flex items-center justify-between">
            <InfoText
              text={t('components.store.customizations')}
              className="!text-light-text-primary !text-base !font-semibold dark:!text-dark-text-primary"
            />

            <MainCardItem suspended={false} title="" />
          </div>

          <Table
            columns={columns}
            className="customTable"
            dataSource={dataSource?.filter((d) => !!d.value)}
            pagination={false}
            showHeader={false}
            size="middle"
          />
        </>
      )}

      {imageSrc && (
        <Image
          width={200}
          style={{ display: 'none' }}
          alt="basic image"
          preview={{
            src: imageSrc,
            visible: !!imageSrc,
            onVisibleChange: () => {
              setImageSrc(undefined);
            },
          }}
        />
      )}
    </div>
  );
}

const ImageButton = ({
  name,
  onClick,
}: {
  name: string;
  onClick: () => void;
}) => {
  return (
    <button
      className="ease-in-out duration-300 transition-all hover:!text-light-button-hover"
      onClick={onClick}
    >
      {name}
    </button>
  );
};
